import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "@/content";
import { useNow } from "@/lib/hooks";
import { Reveal } from "@/components/Motion";
import { V3Section, V3Eyebrow, V3Title } from "@/sections/v3/shared";

// 6. Parcours — rangée horizontale de 7 cartes hautes (scroll-snap + boutons).
export const Journey3 = () => {
  const j = useContent().journey;
  const ui = useContent().ui;
  const track = useRef(null);

  const slide = (dir) => {
    const el = track.current;
    if (!el) return;
    const rtl = document.documentElement.dir === "rtl";
    const step = 350 * dir * (rtl ? -1 : 1);
    el.scrollBy({ left: step, behavior: "smooth" });
  };

  return (
    <V3Section id={j.id} bg="#141011" className="overflow-hidden py-24 md:py-32">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <V3Eyebrow>{j.eyebrow}</V3Eyebrow>
          <V3Title testid="journey-title" className="max-w-xl md:text-[2rem]">{j.title}</V3Title>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => slide(-1)} aria-label={ui.prev} data-testid="journey-prev" className="border border-copper/40 p-3.5 text-white/85 transition-colors duration-300 hover:bg-plum hover:text-white">
            <ChevronLeft size={20} strokeWidth={1.2} />
          </button>
          <button type="button" onClick={() => slide(1)} aria-label={ui.next} data-testid="journey-next" className="border border-copper/40 p-3.5 text-white/85 transition-colors duration-300 hover:bg-plum hover:text-white">
            <ChevronRight size={20} strokeWidth={1.2} />
          </button>
        </div>
      </div>

      <div
        ref={track}
        data-testid="journey-track"
        className="v3-snap no-scrollbar -mx-6 mt-14 flex overflow-x-auto px-6 md:-mx-12 md:px-12 lg:-mx-16 lg:px-16"
      >
        {j.steps.map((s, i) => (
          <article
            key={s.n}
            data-testid={`journey-card-${i + 1}`}
            className="v3-snap-item relative flex min-h-[420px] w-[290px] shrink-0 flex-col border-s border-copper/25 ps-8 pe-8 pt-10 md:w-[340px] md:pe-12"
          >
            {/* Filet continu + point au-dessus de chaque carte */}
            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-copper/30" />
            <span aria-hidden="true" className="absolute -top-[5px] start-8 h-[9px] w-[9px] bg-copper" />
            <span aria-hidden="true" className="pointer-events-none select-none font-serif text-[5.5rem] leading-none text-blush/20">{s.n}</span>
            <h3 className="mt-8 font-serif text-[1.25rem] leading-snug text-white md:text-[1.35rem]">{s.title}</h3>
            <p className="mt-4 text-[0.95rem] leading-relaxed text-white/75">{s.text}</p>
            <span aria-hidden="true" className="mt-auto pt-8 font-sans text-[0.75rem] tabular-nums tracking-[0.25em] text-copper/70">
              {s.n} / {j.steps[j.steps.length - 1].n}
            </span>
          </article>
        ))}
      </div>
    </V3Section>
  );
};

// 7. Dates clés — tableau d'agenda vertical (pas de frise horizontale).
export const Dates3 = () => {
  const d = useContent().dates;
  const ui = useContent().ui;
  const now = useNow(60000);
  const nextIdx = d.items.findIndex((it) => new Date(`${it.end}T23:59:59`).getTime() >= now);

  return (
    <V3Section id={d.id} bg="#0B0A0A" className="py-24 md:py-32">
      <V3Eyebrow>{d.eyebrow}</V3Eyebrow>
      <V3Title testid="dates-title">{d.title}</V3Title>

      <div className="mt-14" data-testid="dates-table">
        {d.items.map((it, i) => (
          <Reveal key={it.title} delay={Math.min(i * 0.04, 0.2)}>
            <div
              data-testid={`dates-row-${i + 1}`}
              className={`grid gap-2 border-t border-copper/25 py-6 last:border-b md:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] md:items-baseline md:gap-8 ${i === nextIdx ? "bg-plum/15" : ""}`}
            >
              <p className="flex flex-wrap items-center gap-3 font-serif text-[1.35rem] leading-tight text-blush md:text-[1.5rem]">
                {it.date}
                {i === nextIdx && (
                  <span className="border border-copper px-2.5 py-1 font-sans text-[0.75rem] uppercase tracking-[0.2em] text-copper" data-testid="dates-next-badge">
                    {ui.nextDate}
                  </span>
                )}
              </p>
              <h3 className="text-[0.8rem] uppercase leading-relaxed tracking-[0.22em] text-white">{it.title}</h3>
              <p className="text-[0.95rem] leading-relaxed text-white/75">{it.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 max-w-3xl">
        <p className="text-[0.95rem] leading-relaxed text-white/70">{d.nb}</p>
      </Reveal>
    </V3Section>
  );
};
